import { useEffect, useRef, useState } from 'react';

/**
 * Announcements, rationed.
 *
 * The rule that matters: never one announcement per event. A busy morning
 * produces a dozen board changes a minute, and a screen-reader user who is told
 * about every one of them cannot hear anything else — including the message they
 * were reading. So this holds the latest summary and speaks it at most once
 * every `everyMs`, and only when the text has actually changed.
 *
 * Politeness is deliberate too: `polite` here, `assertive` reserved for the
 * toaster (a lost claim, a failed send), which is the only thing in the console
 * urgent enough to interrupt.
 */
export function useThrottledAnnouncement(text: string, everyMs = 5000): string {
  const [announced, setAnnounced] = useState('');
  const lastAt = useRef(0);
  const pending = useRef<string>('');

  useEffect(() => {
    pending.current = text;
    if (text === '' || text === announced) return;

    const wait = Math.max(0, lastAt.current + everyMs - Date.now());
    const id = setTimeout(() => {
      lastAt.current = Date.now();
      setAnnounced(pending.current);
    }, wait);

    return () => {
      clearTimeout(id);
    };
  }, [text, announced, everyMs]);

  return announced;
}

export function LiveRegion({ text }: { text: string }) {
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {text}
    </div>
  );
}
